const StorageTypes = ['localStorage', 'sessionStorage'] as const

export type StorageType = (typeof StorageTypes)[number]

const DefaultStorageType: StorageType = 'localStorage'

function getBrowserStorage(storageType: StorageType = DefaultStorageType): Storage | null {
    if (typeof window === 'undefined' && !hasLoggedBrowserStorageNotAvailableWarning) {
        logOnceBrowserStorageNotAvailableWarning()
    }

    try {
        return window[storageType]
    } catch (err) {
        logOnceBrowserStorageNotAvailableWarning(err)
        return null
    }
}

let hasLoggedBrowserStorageNotAvailableWarning = false

function logOnceBrowserStorageNotAvailableWarning(extraInfo?: unknown) {
    if (!hasLoggedBrowserStorageNotAvailableWarning) {
        console.warn(
            `Browser storage is not available.
Possible reasons: running this website in an iframe, in an incognito browser session, or using too strict browser privacy settings.`,
            extraInfo ?? '',
        )
        hasLoggedBrowserStorageNotAvailableWarning = true
    }
}

function getItem(
    key: string,
    options?: { persistence?: StorageType },
): { isSuccessful: false } | { isSuccessful: true; item: string | null } {
    const browserStorage = getBrowserStorage(options?.persistence)
    if (browserStorage === null) return { isSuccessful: false }

    try {
        const item = browserStorage.getItem(key)
        return { isSuccessful: true, item }
    } catch (e) {
        console.error(`Browser storage error, can't get key=${key}`, e)
        return { isSuccessful: false }
    }
}

/**
 * Returns isSuccessful
 */
function setItem(key: string, value: string, options?: { persistence?: StorageType }): boolean {
    const browserStorage = getBrowserStorage(options?.persistence)
    if (browserStorage === null) return false

    try {
        browserStorage.setItem(key, value)
        return true
    } catch (e) {
        console.error(`Browser storage error, can't set ${key}=${value}`, e)
        return false
    }
}

export const DEFAULT_ENCODER = JSON.stringify
export const DEFAULT_DECODER = JSON.parse

type GetValueParams<T> = {
    key: string
    getItemValidation?: (v: T) => boolean
    /**
     * Should be synchronous for the moment
     */
    decode?: (str: string) => unknown
    persistence?: StorageType
}

/**
 * isSuccessful = no error, not null and valid
 */
export function getValue<T>({
    key,
    getItemValidation,
    decode = DEFAULT_DECODER,
    persistence = DefaultStorageType,
}: GetValueParams<T>): { isSuccessful: false } | { isSuccessful: true; value: T; item: string } {
    const result = getItem(key, { persistence })

    if (!(result.isSuccessful && result.item !== null)) return { isSuccessful: false }

    let decoded: unknown

    try {
        decoded = decode(result.item)
    } catch (e) {
        console.error(`Failed to parse ${persistence} of key=${key}'s value`, e)
        return { isSuccessful: false }
    }

    try {
        if (getItemValidation && !getItemValidation(decoded as T)) {
            console.error(`${persistence} of key=${key}'s value is not valid`)
            return { isSuccessful: false }
        }
    } catch (e) {
        console.error(`Failed to validate ${persistence} of key=${key}'s value`, e)
        return { isSuccessful: false }
    }

    return { isSuccessful: true, value: decoded as T, item: result.item }
}

type SetItemByValueParams<T> = {
    key: string
    value: T
    /**
     * Should be synchronous for the moment
     */
    encode?: (v: T) => string
    persistence?: StorageType
}

export function setItemByValue<T>({
    key,
    value,
    encode = DEFAULT_ENCODER,
    persistence = DefaultStorageType,
}: SetItemByValueParams<T>): { isSuccessful: false } | { isSuccessful: true; encoded: string } {
    let encoded: string

    try {
        encoded = encode(value)
    } catch (e) {
        console.error(`Failed to encode value: ${value}`, e)
        return { isSuccessful: false }
    }

    const isSetItemSuccessful = setItem(key, encoded, { persistence })

    return isSetItemSuccessful ? { isSuccessful: true, encoded } : { isSuccessful: false }
}
